/**
 * SEASUN Effects System
 *
 * Provides various animations and effects for elements wrapped in
 * the SEASUN Effects block.
 *
 * Features:
 * - Efficiently uses IntersectionObserver for scroll animations
 * - Supports multiple animation types (fade-up, fade-in, etc.)
 * - Includes continuous animations (heartbeat)
 * - Respects user's prefers-reduced-motion setting
 * - Progressively enhances - content visible without JS
 * - Handles both initial page load and dynamic section loading
 * - Uses requestAnimationFrame and will-change for smoother animations
 */

(function() {
  // Function to initialize all animations
  function initAllAnimations() {
    // Skip all animations if user prefers reduced motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      // Apply active class to all animated elements immediately
      document.querySelectorAll('[data-anim]').forEach(function(el) {
        el.classList.add('anim-active');
      });
      return; // Exit early - no animations needed
    }

    // Initialize scroll animations
    initScrollAnimations();

    // Initialize continuous animations
    initContinuousAnimations();
  }

  /**
   * Initializes continuous animations (heartbeat, etc.)
   * These animations don't rely on scroll position and start immediately
   */
  function initContinuousAnimations() {
    // Find all elements with continuous animations
    const continuousAnimElements = document.querySelectorAll('[data-anim="heartbeat"]');

    // Apply will-change to each element
    continuousAnimElements.forEach(function(element) {
      // Apply appropriate will-change property
      element.style.willChange = 'transform';

      // For continuous animations, we'll release the GPU resources after 10 seconds
      // This is a good balance between visual quality and performance
      setTimeout(function() {
        // Only remove if element is still in the DOM
        if (element.isConnected) {
          element.style.willChange = 'auto';
        }
      }, 10000);
    });
  }

  /**
   * Initializes scroll-triggered animations using IntersectionObserver
   * Handles both existing and dynamically added elements
   */
  function initScrollAnimations() {
    // Create observer with threshold that triggers when element is 20% visible
    const observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          // Get animation type
          const animType = entry.target.getAttribute('data-anim');

          // Skip non-scroll animations (continuous animations are handled separately)
          if (animType === 'none' || animType === 'heartbeat') {
            return;
          }

          // Apply will-change before animation starts (best practice)
          if (animType === 'fade-up' || animType === 'fade-down' ||
              animType === 'fade-left' || animType === 'fade-right' ||
              animType === 'scale-in') {
            entry.target.style.willChange = 'transform, opacity';
          } else if (animType === 'fade-in') {
            entry.target.style.willChange = 'opacity';
          }

          // Small delay to allow browser optimization
          setTimeout(function() {
            // Use requestAnimationFrame for smoother animation
            requestAnimationFrame(function() {
              // Add active class to trigger animation
              entry.target.classList.add('anim-active');
            });
          }, 50);

          // Stop observing after animation triggers
          observer.unobserve(entry.target);

          // Listen for transition end to release GPU resources
          entry.target.addEventListener('transitionend', function transitionHandler() {
            // Release GPU resources after animation completes
            entry.target.style.willChange = 'auto';
            // Remove the listener to avoid memory leaks
            entry.target.removeEventListener('transitionend', transitionHandler);
          });
        }
      });
    }, {
      threshold: 0.2,
      rootMargin: '0px 0px -10% 0px' // Trigger when element is 10% above viewport bottom
    });

    // Function to observe new elements
    function observeAnimElements() {
      // Get scroll animation elements that aren't already active
      const scrollAnimElements = document.querySelectorAll(
        '[data-anim]:not(.anim-active):not([data-anim="none"]):not([data-anim="heartbeat"])'
      );

      // Observe each element
      scrollAnimElements.forEach(function(el) {
        observer.observe(el);
      });
    }

    // Initial observation
    observeAnimElements();

    // Set up MutationObserver to watch for dynamically added sections
    const mutationObserver = new MutationObserver(function(mutations) {
      let shouldCheckForAnimElements = false;

      mutations.forEach(function(mutation) {
        // Check for added nodes that might contain animation elements
        if (mutation.addedNodes.length) {
          shouldCheckForAnimElements = true;
        }
      });

      // Only run the check once per batch of mutations
      if (shouldCheckForAnimElements) {
        requestAnimationFrame(function() {
          initContinuousAnimations(); // Handle continuous animations
          observeAnimElements(); // Observe scroll animation elements
        });
      }
    });

    // Start observing document for dynamic content changes
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Also listen for Shopify section events
    document.addEventListener('shopify:section:load', function() {
      requestAnimationFrame(function() {
        initContinuousAnimations(); // Handle continuous animations
        observeAnimElements(); // Observe scroll animation elements
      });
    });
  }

  // Run on initial page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllAnimations);
  } else {
    // DOMContentLoaded already fired
    initAllAnimations();
  }
})();